/**
 * auMap.ts — a tiny, dependency-free Australia for thumbnails.
 *
 * The planner's popular-trip cards and the phone empty state draw routes on a
 * drawn map of Australia rather than a Google static map: no API key, no
 * per-load bill, nothing to fail. The coastline is a hand-simplified outline
 * (~110 points) — fine at thumbnail size, not for navigation.
 *
 * Catalogue routes only carry place NAMES ("Melbourne" → "Adelaide"), so a
 * small gazetteer places their ends.
 */

export type LngLat = [number, number];

/** Mainland, clockwise from Cape York. */
const MAINLAND: LngLat[] = [
  [142.5, -10.7], [143.4, -12.3], [143.5, -13.8], [144.5, -14.3], [145.3, -15.4],
  [145.5, -16.4], [145.95, -17.1], [146.1, -18.3], [146.8, -19.2], [147.6, -19.4],
  [148.6, -20.1], [149.2, -21.1], [149.6, -22.3], [150.5, -22.4], [150.9, -23.5],
  [151.7, -24.0], [152.4, -24.8], [153.0, -25.2], [153.1, -26.1], [153.15, -27.1],
  [153.45, -28.1], [153.6, -28.65], [153.35, -29.5], [153.15, -30.3], [152.95, -31.4],
  [152.55, -32.4], [151.75, -32.95], [151.3, -33.75], [151.15, -34.2], [150.85, -34.9],
  [150.55, -35.6], [150.15, -36.3], [149.95, -37.1], [149.95, -37.55], [149.1, -37.8],
  [148.15, -37.85], [147.5, -38.1], [146.95, -38.6], [146.4, -39.1], [145.9, -38.7],
  [145.45, -38.5], [145.0, -38.45], [144.65, -38.3], [144.4, -38.25], [143.9, -38.65],
  [143.5, -38.85], [142.95, -38.6], [142.4, -38.4], [141.6, -38.35], [140.95, -38.05],
  [140.4, -37.85], [139.75, -37.15], [139.6, -36.6], [139.3, -35.95], [138.9, -35.55],
  [138.45, -35.65], [138.1, -35.65], [138.5, -34.85], [138.25, -34.25], [137.95, -34.45],
  [137.75, -35.05], [137.4, -35.15], [136.9, -35.25], [137.1, -34.8], [137.45, -34.05],
  [137.85, -33.2], [137.8, -32.55], [137.55, -33.0], [137.1, -33.75], [136.55, -34.05],
  [135.95, -34.75], [135.65, -34.6], [135.25, -34.15], [134.85, -33.45], [134.2, -32.8],
  [133.7, -32.15], [132.6, -31.95], [131.15, -31.45], [129.0, -31.65], [127.4, -32.25],
  [126.0, -32.3], [124.75, -32.9], [123.65, -33.85], [122.6, -33.9], [121.9, -33.85],
  [120.8, -33.9], [119.9, -33.95], [119.2, -34.45], [118.3, -34.95], [117.85, -35.1],
  [116.6, -35.0], [115.65, -34.4], [115.05, -34.3], [115.0, -33.55], [115.65, -33.3],
  [115.75, -32.6], [115.75, -31.9], [115.4, -31.0], [115.0, -30.3], [114.95, -29.5],
  [114.6, -28.8], [114.1, -27.7], [113.85, -26.9], [113.45, -26.1], [113.65, -24.9],
  [113.4, -24.1], [113.75, -23.2], [113.9, -22.3], [114.15, -21.8], [114.6, -22.1],
  [115.1, -21.6], [116.0, -21.0], [116.8, -20.6], [117.9, -20.65], [118.6, -20.3],
  [119.6, -20.0], [120.9, -19.6], [121.5, -19.0], [122.2, -18.0], [122.3, -17.2],
  [122.9, -16.4], [123.55, -17.2], [123.9, -16.3], [124.6, -16.0], [124.85, -15.3],
  [125.5, -14.5], [126.2, -14.2], [126.9, -13.75], [127.7, -14.2], [128.15, -14.9],
  [128.5, -15.0], [129.6, -14.9], [129.85, -13.9], [130.3, -13.1], [130.85, -12.4],
  [131.3, -12.15], [132.4, -12.2], [132.6, -11.5], [133.2, -11.75], [134.0, -11.95],
  [135.0, -12.2], [135.85, -11.95], [136.6, -12.0], [136.95, -12.35], [136.55, -12.9],
  [135.95, -13.3], [135.85, -14.2], [135.45, -14.9], [136.3, -15.5], [137.0, -15.95],
  [137.75, -16.4], [138.6, -16.8], [139.3, -17.4], [140.15, -17.7], [140.85, -17.45],
  [141.3, -16.4], [141.55, -15.1], [141.55, -13.6], [141.65, -12.95], [141.9, -12.2],
  [142.15, -11.0],
];

const TASMANIA: LngLat[] = [
  [144.65, -40.7], [145.3, -40.85], [146.05, -41.1], [146.6, -41.1], [147.3, -40.95],
  [148.3, -40.95], [148.3, -41.9], [148.0, -42.6], [147.95, -43.15], [147.3, -43.25],
  [146.95, -43.6], [146.05, -43.55], [145.5, -42.95], [145.2, -42.25], [144.85, -41.6],
  [144.65, -41.0],
];

/** The coastline rings, [lng, lat]: the mainland, then Tasmania. */
export const AU_COAST: LngLat[][] = [MAINLAND, TASMANIA];

/** Every frame these thumbnails use: the continent plus Tasmania. */
export const AU_FRAME = { west: 112.6, east: 154.2, north: -10.2, south: -43.9 };

const mercY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));

export interface Projector {
  (p: LngLat): [number, number];
  width: number;
  height: number;
}

/**
 * Web-Mercator projector that fits AU_FRAME into a box `width` wide (height
 * follows from the aspect), so thumbnails look like the real map beside them.
 */
export function makeProjector(width: number): Projector {
  const { west, east, north, south } = AU_FRAME;
  const kx = width / (east - west);
  // Mercator: y-units per radian of mercY match x-units per radian of lng.
  const ky = kx * (180 / Math.PI);
  const top = mercY(north);
  const height = (top - mercY(south)) * ky;
  const project = ((p: LngLat) => [(p[0] - west) * kx, (top - mercY(p[1])) * ky]) as Projector;
  project.width = width;
  project.height = height;
  return project;
}

function pathFor(points: LngLat[], project: Projector): string {
  return (
    points
      .map((p, i) => {
        const [x, y] = project(p);
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join('') + 'Z'
  );
}

/** SVG path data for the mainland + Tasmania. */
export function australiaPath(project: Projector): string {
  return pathFor(MAINLAND, project) + pathFor(TASMANIA, project);
}

/** Where the catalogue's route ends are. Keys are normalised (see placeKey). */
const PLACES: Record<string, LngLat> = {
  adelaide: [138.6, -34.93],
  albany: [117.88, -35.02],
  alicesprings: [133.88, -23.7],
  armidale: [151.67, -30.51],
  arkaroola: [139.34, -30.31],
  bairnsdale: [147.61, -37.83],
  brisbane: [153.03, -27.47],
  broome: [122.24, -17.96],
  byronbay: [153.61, -28.64],
  cairns: [145.77, -16.92],
  canberra: [149.13, -35.28],
  capetribulation: [145.46, -16.09],
  coffsharbour: [153.11, -30.3],
  darwin: [130.84, -12.46],
  derby: [123.63, -17.31],
  esperance: [121.89, -33.86],
  exmouth: [114.13, -21.93],
  fraserisland: [153.13, -25.27],
  geraldton: [114.61, -28.78],
  goldcoast: [153.4, -28.02],
  hobart: [147.33, -42.88],
  kgari: [153.13, -25.27],
  katherine: [132.26, -14.47],
  katoomba: [150.31, -33.71],
  kingscote: [137.64, -35.66],
  kununurra: [128.74, -15.77],
  launceston: [147.14, -41.43],
  melbourne: [144.96, -37.81],
  noosa: [153.09, -26.39],
  nowra: [150.6, -34.88],
  penneshaw: [137.94, -35.72],
  perth: [115.86, -31.95],
  portaugusta: [137.77, -32.49],
  sydney: [151.21, -33.87],
  townsville: [146.82, -19.26],
  uluru: [131.04, -25.34],
  wangaratta: [146.31, -36.36],
  warrnambool: [142.48, -38.38],
};

export const placeKey = (name: string) => name.toLowerCase().replace(/[^a-z]/g, '');

export function placeLngLat(name?: string | null): LngLat | null {
  if (!name) return null;
  return PLACES[placeKey(name)] ?? null;
}

/** A route's two ends, or null when either end is unknown. */
export function routeEnds(route: { from?: string; to?: string }): [LngLat, LngLat] | null {
  const a = placeLngLat(route.from);
  const b = placeLngLat(route.to);
  return a && b ? [a, b] : null;
}

/**
 * A gentle arc between two projected points (quadratic curve bowed to one
 * side), so a drawn route reads as a drive rather than a ruler line. Very
 * short routes (both ends within a few px) become a single dot's worth of arc.
 */
export function arcPath(a: [number, number], b: [number, number], bow = 0.18): string {
  const mx = (a[0] + b[0]) / 2;
  const my = (a[1] + b[1]) / 2;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  // Perpendicular offset, always bowing "up" on screen for a consistent look.
  const sign = dx >= 0 ? -1 : 1;
  const cx = mx + sign * dy * bow;
  const cy = my - sign * dx * bow;
  return `M${a[0].toFixed(1)} ${a[1].toFixed(1)}Q${cx.toFixed(1)} ${cy.toFixed(1)} ${b[0].toFixed(1)} ${b[1].toFixed(1)}`;
}
